import { useState, useEffect, useCallback } from 'react';
import { Menu, X } from 'lucide-react';

const CALENDLY_URL = 'https://calendly.com/matthew-trottstudios';
const LOGO_URL = '/trott-logo.png';
const SCROLL_THRESHOLD = 20;

const NAV_LINKS = [
  { name: 'Services', href: '#services' },
  { name: 'Process', href: '#process' },
];

/**
 * NavLogo - Logo component for navigation
 */
const NavLogo = () => (
  <a
    href="/"
    data-testid="nav-logo"
    className="flex items-center group"
  >
    <img 
      src={LOGO_URL} 
      alt="TrottStudios" 
      className="h-[72px] md:h-20 w-auto max-w-[280px] md:max-w-[360px] object-contain transition-transform group-hover:scale-105"
    />
  </a>
);

/**
 * DesktopNav - Desktop navigation links and CTA
 */
const DesktopNav = ({ onScrollToSection }) => (
  <div className="hidden lg:flex items-center gap-8">
    {NAV_LINKS.map((link) => (
      <a
        key={link.name}
        href={link.href}
        onClick={(e) => onScrollToSection(e, link.href)}
        data-testid={`nav-link-${link.name.toLowerCase()}`}
        className="text-sm font-medium transition-colors underline-animation text-gray-300 hover:text-[#00D4FF]"
      >
        {link.name}
      </a>
    ))}
    <a
      href={CALENDLY_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="nav-cta-button"
      className="inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-medium bg-[#00D4FF] text-[#1E1E2E] hover:bg-[#00B8E6] transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-[#00D4FF]/25"
    >
      Book a Call
    </a>
  </div>
);

/**
 * MobileMenuButton - Hamburger menu toggle button
 */
const MobileMenuButton = ({ isOpen, onToggle }) => (
  <button
    onClick={onToggle}
    data-testid="mobile-menu-button"
    className="lg:hidden p-2 transition-colors text-white"
    aria-label="Toggle menu"
  >
    {isOpen ? <X size={24} /> : <Menu size={24} />}
  </button>
);

/**
 * MobileNav - Mobile navigation menu
 */
const MobileNav = ({ isOpen, onScrollToSection }) => {
  if (!isOpen) return null;

  return (
    <div
      data-testid="mobile-menu"
      className="lg:hidden bg-[#1E1E2E] border-t border-[#00D4FF]/20 py-5 rounded-b-2xl"
    >
      <div className="flex flex-col gap-4">
        {NAV_LINKS.map((link) => (
          <a
            key={link.name}
            href={link.href}
            onClick={(e) => onScrollToSection(e, link.href)}
            data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
            className="text-base font-medium text-gray-300 hover:text-[#00D4FF] transition-colors py-2 px-2"
          >
            {link.name}
          </a>
        ))}
        <a
          href={CALENDLY_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="mobile-nav-cta-button"
          className="inline-flex items-center justify-center rounded-full px-5 py-3 text-base font-medium bg-[#00D4FF] text-[#1E1E2E] hover:bg-[#00B8E6] transition-all duration-300 mt-2 mx-2"
        >
          Book a Call
        </a>
      </div>
    </div>
  );
};

/**
 * Navigation - Main navigation component
 */
export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > SCROLL_THRESHOLD);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollToSection = useCallback((e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-[#1E1E2E] ${
        isScrolled ? 'shadow-lg shadow-black/10' : ''
      }`}
    >
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          <NavLogo />
          <DesktopNav onScrollToSection={scrollToSection} />
          <MobileMenuButton isOpen={isMobileMenuOpen} onToggle={toggleMobileMenu} />
        </div>
        <MobileNav isOpen={isMobileMenuOpen} onScrollToSection={scrollToSection} />
      </div>
    </nav>
  );
};

export default Navigation;
